import { css } from '@emotion/react';
import type { ReactElement, RefObject } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import IconChevronDown from '~icons/ci/chevron-down';

import { SelectorSelect } from '@adflow/frontend/ui-form';
import { theme } from '@adflow/frontend/ui-theme';
import type { DataTableItemType } from '@adflow/shared/util-column-templates';

import { ListPageSelector } from './ListPageSelector';
import { ListPaginationTotal } from './ListPaginationTotal';

const styles = {
	container: css({
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingLeft: '1rem',
		paddingRight: '1rem',
		paddingTop: '0.5rem',
		paddingBottom: '0.5rem',
	}),
	buttonsWrap: css({ display: 'flex', alignItems: 'center' }),
	text: css({
		color: theme.colors.text.primary,
		fontWeight: '400',
	}),
	dropDownBtnOptionsWrap: css({ position: 'relative', marginLeft: '1rem' }),
	dropdownBtn: css({
		backgroundColor: theme.colors.background.paper,
		borderColor: theme.colors.input.border,
		paddingLeft: '0.75rem',
		paddingRight: '0.75rem',
		paddingTop: '0.25rem',
		paddingBottom: '0.25rem',
		borderWidth: '1px',
		display: 'flex',
		alignItems: 'center',
		outline: '2px solid transparent',
		outlineOffset: '2px',
		':focus': { outline: '2px solid transparent', outlineOffset: '2px' },
	}),

	dropdownList: css({
		boxShadow: '0px 4px 8px #0000001f',
		position: 'absolute',
		bottom: '0px',
		left: '0px',
		right: '0px',
		outline: '2px solid transparent',
		outlineOffset: '2px',
	}),

	openList: css({
		borderColor: theme.colors.input.border,
		borderWidth: '1px',
		outline: '2px solid transparent',
		outlineOffset: '2px',
	}),
	dropdownOption: css({ padding: '0.5rem', textAlign: 'left' }),
};

function useRowsOptions(
	rowsPerPage: number,
	rowsPerPageOptions: number[]
): [string[], string, Record<string, number | null>] {
	const { t } = useTranslation('common');

	return useMemo<[string[], string, Record<string, number | null>]>(() => {
		// eslint-disable-next-line unicorn/no-array-reduce
		const map = rowsPerPageOptions.reduce<Record<string, number | null>>(
			(result, rows) => ({
				...result,
				[t('itemsPerPage', { itemsPerPage: rows })]: rows,
			}),
			{ [t('auto')]: null }
		);

		const options = Object.keys(map);
		const result = Object.entries(map).find(([, rows]) => rows === rowsPerPage);
		const selectedOption = result
			? result[0]
			: t('itemsPerPage', { itemsPerPage: rowsPerPage });

		return [options, selectedOption, map];
	}, [rowsPerPageOptions, rowsPerPage, t]);
}

interface ListPaginationProps {
	rowsPerPageOptions: number[];
	totalItems?: number;
	itemType?: DataTableItemType;
	rowsPerPage: number;
	page: number;
	sidebarStyle?: boolean;
	onChangePage: (newPage: number) => void;
	onChangeRowsPerPage: (newRowsPerPage: number | null) => void;
	exportButton?: ReactElement;
	tableFooter?: ReactElement;
	containerRef?: RefObject<HTMLDivElement>;
}

export function ListPagination({
	rowsPerPage,
	rowsPerPageOptions,
	totalItems,
	page,
	onChangePage,
	onChangeRowsPerPage,
	sidebarStyle,
	itemType,
	exportButton,
	tableFooter,
	containerRef,
}: ListPaginationProps) {
	const [selectorOptions, selectedOption, selectorOptionsLUT] = useRowsOptions(
		rowsPerPage,
		rowsPerPageOptions
	);

	return (
		<div
			css={[
				styles.container,
				sidebarStyle &&
					css({
						flexDirection: 'column',
						alignItems: 'flex-start',
						paddingBottom: '1.25rem',
					}),
			]}
			ref={containerRef}
		>
			<div css={styles.buttonsWrap}>
				<ListPaginationTotal
					totalItems={totalItems}
					itemType={itemType}
					sidebarView
				/>

				{sidebarStyle ? null : (
					<SelectorSelect
						items={selectorOptions}
						value={selectedOption}
						onChange={(option) => {
							const value = selectorOptionsLUT[
								option
							] as (typeof selectorOptionsLUT)[number];

							onChangeRowsPerPage(value);
						}}
						testid="pagination-rows-per-page-selector"
						Icon={IconChevronDown}
					/>
				)}
			</div>
			{exportButton ? (
				<>
					<div css={{ paddingLeft: '1.5rem' }}>{exportButton}</div>
					<div css={{ flexGrow: '1' }} />
				</>
			) : null}

			{tableFooter ?? null}
			<ListPageSelector
				totalItems={totalItems}
				rowsPerPage={rowsPerPage}
				page={page}
				onChangePage={onChangePage}
				delta={sidebarStyle ? 0 : undefined}
			/>
		</div>
	);
}
